import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import Layout from '../components/Layout';
import AdDisplay from '../components/AdDisplay';
import Price from '../components/Price';
import ShareButton from '../components/ShareButton';
import DateInput from '../components/DateInput';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { TrendingUp, TrendingDown, Target, DollarSign, Plus, History, Layers } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentBets, setRecentBets] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyChartPeriod, setDailyChartPeriod] = useState('30'); // 7, 14, 30, 90, 365, custom
  const [monthlyChartPeriod, setMonthlyChartPeriod] = useState('6'); // 6, 12, all
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const dailyChartRef = useRef(null);
  const monthlyChartRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [dailyChartPeriod, monthlyChartPeriod, customDateRange]);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      // Build chart params based on selected periods
      const chartParams = new URLSearchParams();
      if (dailyChartPeriod === 'custom' && customDateRange.start && customDateRange.end) {
        chartParams.append('daily_period', 'custom');
        chartParams.append('start_date', customDateRange.start);
        chartParams.append('end_date', customDateRange.end);
      } else {
        chartParams.append('daily_period', dailyChartPeriod);
      }
      chartParams.append('monthly_period', monthlyChartPeriod);

      const [statsRes, betsRes, subRes, chartsRes] = await Promise.all([
        axios.get(`${API}/reports/statistics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/bets`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/dashboard-charts?${chartParams.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      console.log('=== DASHBOARD DATA DEBUG ===');
      console.log('Stats:', statsRes.data);
      console.log('Bets count:', betsRes.data.length);
      console.log('Charts data:', chartsRes.data);
      console.log('Last days length:', chartsRes.data.last_30_days?.length);
      console.log('Monthly results length:', chartsRes.data.monthly_results?.length);
      console.log('Outcome summary length:', chartsRes.data.outcome_summary?.length);
      console.log('Outcome summary:', chartsRes.data.outcome_summary);
      console.log('=== END DEBUG ===');

      setStats(statsRes.data);
      // Sort bets by date (most recent first) and take top 5
      const sortedBets = [...betsRes.data].sort((a, b) => {
        return new Date(b.bet_date) - new Date(a.bet_date);
      });
      setRecentBets(sortedBets.slice(0, 5));
      setSubscription(subRes.data);
      setChartData(chartsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="dashboard-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Dashboard</h1>
              <p className="text-gray-300 text-sm sm:text-base">Welcome back, {user?.name}!</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => navigate('/bets')} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full shrink-0">
                <History className="w-5 h-5 mr-2" />
                View History
              </Button>
              <Button data-testid="add-bet-button" onClick={() => navigate('/bets/add')} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full btn-hover shrink-0">
                <Plus className="w-5 h-5 mr-2" />
                Add Bet
              </Button>
            </div>
          </div>

          {/* Subscription Alert */}
          {subscription && subscription.plan === 'free' && !subscription.can_add_bet && (
            <div data-testid="subscription-alert" className="glass-dark p-4 rounded-xl mb-6 border-l-4 border-yellow-500">
              <p className="text-yellow-300">
                You've reached your bet limit ({subscription.bets_used}/{subscription.bets_limit}).
                <Button onClick={() => navigate('/subscription')} variant="link" className="text-blue-400 ml-2">Upgrade to Premium</Button>
              </p>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-700/20 border-blue-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total Bets</p>
                    <p data-testid="stat-total-bets" className="text-3xl font-bold text-white">{stats?.total_bets || 0}</p>
                  </div>
                  <Target className="w-12 h-12 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/20 to-green-700/20 border-green-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Win Rate</p>
                    <p data-testid="stat-win-rate" className="text-3xl font-bold text-white">{stats?.win_rate || 0}%</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-700/20 border-purple-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">ROI</p>
                    <p data-testid="stat-roi" className="text-3xl font-bold text-white">{stats?.roi || 0}%</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br ${stats?.total_returns >= 0 ? 'from-emerald-500/20 to-emerald-700/20 border-emerald-500/30' : 'from-red-500/20 to-red-700/20 border-red-500/30'} stat-card`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Profit/Loss</p>
                    <p data-testid="stat-profit-loss" className="text-3xl font-bold text-white">
                      <Price amount={stats?.total_returns || 0} />
                    </p>
                  </div>
                  <DollarSign className={`w-12 h-12 ${stats?.total_returns >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Ad Spaces - Dashboard Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <AdDisplay page="dashboard" position="row1_ad1" />
            <AdDisplay page="dashboard" position="row1_ad2" />
          </div>

          {/* Charts Section - Simple HTML/CSS Version */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Daily Profit/Loss Chart with Period Selector */}
            <Card ref={dailyChartRef} className="glass-dark border-white/10">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Daily Profit/Loss Tracker</CardTitle>
                  <div className="flex items-center gap-2">
                    <ShareButton 
                      elementRef={dailyChartRef}
                      title="My Betting Performance"
                      description="Check out my betting tracker on BetBanky!"
                    />
                    <Select value={dailyChartPeriod} onValueChange={setDailyChartPeriod}>
                      <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 Days</SelectItem>
                      <SelectItem value="14">14 Days</SelectItem>
                      <SelectItem value="30">30 Days</SelectItem>
                      <SelectItem value="90">90 Days</SelectItem>
                      <SelectItem value="365">1 Year</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  </div>
                </div>
                {dailyChartPeriod === 'custom' && (
                  <div className="flex gap-2 mt-3">
                    <DateInput
                      label="Start Date"
                      value={customDateRange.start}
                      onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                    />
                    <DateInput
                      label="End Date"
                      value={customDateRange.end}
                      onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {chartData && chartData.last_30_days && chartData.last_30_days.length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-center mb-4">
                      <p className="text-sm text-gray-400">Final Cumulative</p>
                      <p className={`text-3xl font-bold ${chartData.last_30_days[chartData.last_30_days.length - 1]?.cumulative >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        <Price amount={chartData.last_30_days[chartData.last_30_days.length - 1]?.cumulative || 0} />
                      </p>
                    </div>
                    <div className="grid grid-cols-5 gap-1 text-xs">
                      {chartData.last_30_days.slice(-10).map((day, idx) => (
                        <div key={idx} className="glass p-2 rounded text-center">
                          <p className="text-gray-400">{day.date.split('-')[2]}</p>
                          <p className={`font-bold ${day.cumulative >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <Price amount={day.cumulative} decimals={0} />
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">Last {Math.min(10, chartData.last_30_days.length)} days shown</p>
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-16">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Outcome Summary - Linked to Daily Chart Period */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">
                  Outcome Summary
                  <span className="text-sm text-gray-400 font-normal ml-2">
                    ({dailyChartPeriod === 'custom' ? 'Custom Range' : 
                      dailyChartPeriod === '7' ? 'Last 7 Days' :
                      dailyChartPeriod === '14' ? 'Last 14 Days' :
                      dailyChartPeriod === '30' ? 'Last 30 Days' :
                      dailyChartPeriod === '90' ? 'Last 90 Days' :
                      dailyChartPeriod === '365' ? 'Last Year' : ''})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chartData && chartData.outcome_summary && chartData.outcome_summary.length > 0 ? (
                  <div className="space-y-4">
                    {chartData.outcome_summary.map((item, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white font-medium">{item.name}</span>
                          <span className="text-gray-400">{item.percentage}% ({item.value} bets)</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-6">
                          <div
                            className={`h-6 rounded-full flex items-center justify-end pr-2 text-white text-sm font-bold ${
                              item.name === 'Won' ? 'bg-green-500' :
                              item.name === 'Lost' ? 'bg-red-500' : 'bg-gray-500'
                            }`}
                            style={{ width: `${item.percentage}%` }}
                          >
                            {item.percentage > 10 && `${item.percentage}%`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-16">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly Results Bar Chart with Period Selector */}
          <Card ref={monthlyChartRef} className="glass-dark border-white/10 mb-8">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white">Monthly Results</CardTitle>
                <div className="flex items-center gap-2">
                  <ShareButton 
                    elementRef={monthlyChartRef}
                    title="My Monthly Betting Results"
                    description="Check out my monthly betting performance on BetBanky!"
                  />
                  <Select value={monthlyChartPeriod} onValueChange={setMonthlyChartPeriod}>
                    <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">12 Months</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {chartData && chartData.monthly_results && chartData.monthly_results.length > 0 ? (
                <div className="w-full">
                  <div className="grid gap-1 sm:gap-2 items-end" style={{
                    height: '200px',
                    gridTemplateColumns: `repeat(${chartData.monthly_results.length}, 1fr)`
                  }}>
                    {chartData.monthly_results.map((month, idx) => {
                      const maxProfit = Math.max(...chartData.monthly_results.map(m => Math.abs(m.profit)));
                      const heightPercent = maxProfit > 0 ? (Math.abs(month.profit) / maxProfit) * 100 : 10;
                      return (
                        <div key={idx} className="flex flex-col items-center justify-end h-full min-w-0">
                          <div className="text-center mb-1">
                            <p className={`text-xs sm:text-sm font-bold truncate ${month.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              <Price amount={Math.abs(month.profit)} showSymbol={true} />
                            </p>
                          </div>
                          <div 
                            className={`w-full rounded-t transition-all ${month.profit >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ height: `${heightPercent}%`, minHeight: '5px' }}
                          ></div>
                          <p className="text-xs text-gray-400 mt-1 truncate w-full text-center">{month.month}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-16">No data available</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Bets */}
          <Card className="glass-dark border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex justify-between items-center">
                <span>Recent Bets</span>
                <Button data-testid="view-all-bets-button" onClick={() => navigate('/bets')} variant="ghost" className="text-blue-400 hover:text-blue-300">
                  View All
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentBets.length === 0 ? (
                <div data-testid="no-bets-message" className="text-center py-8 text-gray-400">
                  <p>No bets yet. Start tracking your first bet!</p>
                  <Button data-testid="add-first-bet-button" onClick={() => navigate('/bets/add')} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
                    Add Your First Bet
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentBets.map((bet, index) => (
                    <div key={index} data-testid={`recent-bet-${index}`} className="glass p-4 rounded-lg hover:bg-white/10 transition-colors">
                      {/* Accumulator Badge */}
                      {bet.bet_type === 'accumulator' && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            <Layers className="w-3 h-3" />
                            {bet.num_selections || bet.selections?.length || 0}-Fold Accumulator
                          </span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {/* Single bet display */}
                          {bet.bet_type !== 'accumulator' && (
                            <>
                              <p className="text-white font-medium">{bet.home_team} vs {bet.away_team}</p>
                              <p className="text-sm text-gray-400">{bet.league} • {bet.market}</p>
                            </>
                          )}
                          
                          {/* Accumulator selections display */}
                          {bet.bet_type === 'accumulator' && bet.selections && (
                            <div className="space-y-1">
                              {bet.selections.map((sel, selIndex) => (
                                <div key={selIndex} className="flex items-center gap-2">
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    sel.result === 'won' ? 'bg-green-400' :
                                    sel.result === 'lost' ? 'bg-red-400' :
                                    sel.result === 'void' ? 'bg-gray-400' :
                                    'bg-yellow-400'
                                  }`}></span>
                                  <p className="text-white text-sm">
                                    {sel.home_team} vs {sel.away_team}
                                    <span className="text-gray-500 ml-2">• {sel.market} @ {sel.odds?.toFixed(2)}</span>
                                  </p>
                                </div>
                              ))}
                              <p className="text-xs text-gray-500 mt-1">Combined Odds: {bet.odds?.toFixed(2)}</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right ml-4">
                          <p className="text-white font-medium">Stake: <Price amount={bet.stake} /></p>
                          {bet.profit_loss !== null && bet.profit_loss !== undefined && (
                            <p className={`text-sm font-bold ${bet.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              P/L: {bet.profit_loss >= 0 ? '+' : ''}<Price amount={Math.abs(bet.profit_loss)} />
                            </p>
                          )}
                          <span className={`text-sm px-3 py-1 rounded-full ${
                            bet.result === 'won' ? 'bg-green-500/20 text-green-400' :
                            bet.result === 'lost' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {bet.result || 'pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
